import React, { Component, useMemo, useCallback, useRef, useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import Select from 'react-select';
import { Pagination } from '../../../components';

import './Grid.css';

/**
 * MultiSelectFilter — searchable multi-select dropdown that replicates the
 * look of RDG's MultiSelectFilter (which used react-select v1 internally).
 * Uses react-select v5 with `isMulti` — produces chips/tags for selected
 * items, searchable dropdown, no checkboxes.
 */
function MultiSelectFilter({ column, options, onFilterChange }) {
    const selectOptions = useMemo(() => {
        return (options || []).map(opt => ({
            value: typeof opt === 'string' ? opt : String(opt),
            label: typeof opt === 'string' ? opt : String(opt),
        }));
    }, [options]);

    const handleChange = useCallback((selectedOptions) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            onFilterChange({ column, filterTerm: null });
        } else {
            onFilterChange({
                column,
                filterTerm: selectedOptions.map(opt => ({ value: opt.value })),
            });
        }
    }, [column, onFilterChange]);

    return (
        <Select
            isMulti
            name={`filter-${column.key}`}
            options={selectOptions}
            placeholder="Search"
            onChange={handleChange}
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            classNamePrefix="rt-select"
            styles={{
                control: (base) => ({
                    ...base,
                    minHeight: 30,
                    fontSize: '0.85em',
                    backgroundColor: 'var(--primary-background-color)',
                    borderColor: 'var(--border-color)',
                }),
                valueContainer: (base) => ({
                    ...base,
                    padding: '0 4px',
                }),
                multiValue: (base) => ({
                    ...base,
                    fontSize: '0.9em',
                    backgroundColor: 'var(--secondary-background-color)',
                    border: '1px solid var(--border-color)',
                }),
                multiValueLabel: (base) => ({
                    ...base,
                    color: 'var(--primary-text-color)',
                }),
                multiValueRemove: (base) => ({
                    ...base,
                    color: 'var(--secondary-text-color)',
                    ':hover': {
                        backgroundColor: 'var(--border-color)',
                        color: 'var(--primary-text-color)',
                    },
                }),
                input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                    color: 'var(--primary-text-color)',
                }),
                placeholder: (base) => ({
                    ...base,
                    color: 'var(--secondary-text-color)',
                }),
                menu: (base) => ({
                    ...base,
                    backgroundColor: 'var(--primary-background-color)',
                    border: '1px solid var(--border-color)',
                    zIndex: 9999,
                }),
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                }),
                option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                        ? 'var(--primary-color)'
                        : state.isFocused
                            ? 'var(--secondary-background-color)'
                            : 'var(--primary-background-color)',
                    color: state.isSelected
                        ? '#fff'
                        : 'var(--primary-text-color)',
                    fontSize: '0.85em',
                    padding: '6px 10px',
                    cursor: 'pointer',
                }),
                singleValue: (base) => ({
                    ...base,
                    color: 'var(--primary-text-color)',
                }),
                indicatorsContainer: (base) => ({
                    ...base,
                    height: 30,
                }),
                indicatorSeparator: (base) => ({
                    ...base,
                    backgroundColor: 'var(--border-color)',
                }),
                dropdownIndicator: (base) => ({
                    ...base,
                    padding: '4px',
                    color: 'var(--secondary-text-color)',
                }),
                clearIndicator: (base) => ({
                    ...base,
                    padding: '4px',
                    color: 'var(--secondary-text-color)',
                }),
            }}
        />
    );
}

/**
 * Inner functional component that uses react-table v7 hooks.
 * Receives all props from the Grid class wrapper.
 *
 * Key design decisions:
 * - manualSortBy=true because sorting is server-side (API callback)
 * - No client-side pagination (server-side via Pagination component)
 * - Columns mapped from RDG format {key, name} to react-table format {accessor, Header}
 * - Toolbar and filter row rendered INSIDE the grid container to match RDG layout
 */
function GridTable({
    columns,
    rows,
    sortColumns,
    onSortColumnsChange,
    onRowClick,
    minHeight,
    noRowsFallback,
    enableFilter,
    showFilters,
    onToggleFilters,
    onFilterChange,
    filterValues,
    originalColumns,
}) {
    // Map columns from RDG-style {key, name, sortable} to react-table {accessor, Header, disableSortBy}
    const rtColumns = useMemo(() => {
        if (!columns || columns.length === 0) return [];
        return columns.map(col => ({
            accessor: col.key,
            Header: col.name || col.Header || '',
            // react-table: disableSortBy = true means NOT sortable
            disableSortBy: col.sortable === false,
            // Preserve width if specified
            ...(col.width ? { width: col.width } : {}),
            // Keep original column ref for compatibility
            _original: col,
        }));
    }, [columns]);

    const data = useMemo(() => rows || [], [rows]);

    // Map current sort state to react-table initialState format
    const initialSortBy = useMemo(() => {
        if (!sortColumns || sortColumns.length === 0) return [];
        return sortColumns.map(sc => ({
            id: sc.columnKey,
            desc: sc.direction === 'DESC',
        }));
    }, [sortColumns]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows: tableRows,
        prepareRow,
    } = useTable(
        {
            columns: rtColumns,
            data,
            manualSortBy: true,
            disableMultiSort: true,
            initialState: {
                sortBy: initialSortBy,
            },
        },
        useSortBy,
    );

    // ── Column resize state ──────────────────────────────────
    const [columnWidths, setColumnWidths] = useState({});
    const resizeRef = useRef(null);

    const handleResizeStart = useCallback((e, columnId) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const th = e.target.closest('th');
        const startWidth = th ? th.getBoundingClientRect().width : 150;

        const handleMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            const newWidth = Math.max(startWidth + delta, 40);
            setColumnWidths(prev => ({ ...prev, [columnId]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    // Propagate sort changes back to the parent
    const handleHeaderClick = useCallback((column) => {
        if (column.disableSortBy) return;

        const columnKey = column.id;
        // Cycle: NONE → ASC → DESC → NONE
        if (!sortColumns || sortColumns.length === 0) {
            onSortColumnsChange([{ columnKey, direction: 'ASC' }]);
        } else if (sortColumns[0]?.columnKey === columnKey) {
            if (sortColumns[0].direction === 'ASC') {
                onSortColumnsChange([{ columnKey, direction: 'DESC' }]);
            } else {
                onSortColumnsChange([]);
            }
        } else {
            onSortColumnsChange([{ columnKey, direction: 'ASC' }]);
        }
    }, [sortColumns, onSortColumnsChange]);

    // Determine sort indicator for a column
    const getSortIndicator = useCallback((columnId) => {
        if (!sortColumns || sortColumns.length === 0) return '';
        const sc = sortColumns.find(s => s.columnKey === columnId);
        if (!sc) return '';
        return sc.direction === 'ASC' ? ' \u25B2' : ' \u25BC';
    }, [sortColumns]);

    // Build a map of which columns have filterRenderer (for multi-select)
    const filterRendererMap = useMemo(() => {
        if (!originalColumns) return {};
        const map = {};
        originalColumns.forEach(col => {
            if (col.filterRenderer) {
                map[col.key] = true;
            }
        });
        return map;
    }, [originalColumns]);

    return (
        <div className="rt-grid-container">
            {/* Toolbar outside scroll area — stays fixed at top */}
            {enableFilter && (
                <div className="rt-toolbar react-grid-Toolbar">
                    <div className="rt-toolbar-inner">
                        <button
                            className="rt-toolbar-btn btn grid-filter-toggle"
                            data-testid="filter-toggle"
                            onClick={onToggleFilters}>
                            Filter Rows
                        </button>
                    </div>
                </div>
            )}
            <div className="rt-grid" style={{ height: minHeight || 500 }}>
            <table
                {...getTableProps()}
                className="rt-table"
            >
                <colgroup>
                    {rtColumns.map(col => (
                        <col
                            key={col.accessor}
                            style={
                                columnWidths[col.accessor]
                                    ? { width: columnWidths[col.accessor] }
                                    : col.width
                                        ? { width: col.width }
                                        : undefined
                            }
                        />
                    ))}
                </colgroup>
                <thead>
                    {headerGroups.map(headerGroup => {
                        const { key: hgKey, ...hgProps } = headerGroup.getHeaderGroupProps();
                        return (
                            <tr key={hgKey} {...hgProps} className="rt-header-row">
                                {headerGroup.headers.map(column => {
                                    const { key: colKey, ...colProps } = column.getHeaderProps();
                                    const w = columnWidths[column.id] || column.width;
                                    return (
                                        <th
                                            key={colKey}
                                            {...colProps}
                                            className={`rt-header-cell${column.disableSortBy ? '' : ' rt-sortable'}`}
                                            style={w ? { width: w } : undefined}
                                            onClick={() => handleHeaderClick(column)}
                                        >
                                            {column.render('Header')}
                                            <span className="rt-sort-indicator">
                                                {getSortIndicator(column.id)}
                                            </span>
                                            {column._original && column._original.resizable !== false && (
                                                <div
                                                    className="rt-resizer"
                                                    onMouseDown={(e) => handleResizeStart(e, column.id)}
                                                />
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    {/* Filter row inside thead — matches RDG filter row position */}
                    {showFilters && enableFilter && (
                        <tr className="rt-filter-row" data-testid="filter-row">
                            {(columns || []).map(col => (
                                <th key={col.key} className="rt-filter-cell">
                                    {filterRendererMap[col.key] ? (
                                        <MultiSelectFilter
                                            column={col}
                                            options={filterValues && filterValues[col.key] ? filterValues[col.key] : []}
                                            onFilterChange={onFilterChange}
                                        />
                                    ) : (
                                        <input
                                            data-testid="filter-input"
                                            placeholder={`Filter ${col.name || ''}`}
                                            onChange={e =>
                                                onFilterChange({
                                                    column: col,
                                                    filterTerm: e.target.value || null,
                                                })
                                            }
                                            className="rt-filter-input"
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    )}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {tableRows.length === 0 && noRowsFallback ? (
                        <tr>
                            <td colSpan={rtColumns.length}>{noRowsFallback}</td>
                        </tr>
                    ) : (
                        tableRows.map(row => {
                            prepareRow(row);
                            const { key: rowKey, ...rowProps } = row.getRowProps();
                            return (
                                <tr
                                    key={rowKey}
                                    {...rowProps}
                                    className="rt-row"
                                    onClick={() => onRowClick && onRowClick(row.original)}
                                >
                                    {row.cells.map(cell => {
                                        const { key: cellKey, ...cellProps } = cell.getCellProps();
                                        return (
                                            <td
                                                key={cellKey}
                                                {...cellProps}
                                                className="rt-cell"
                                            >
                                                {cell.render('Cell')}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
            </div>
        </div>
    );
}

/**
 * Grid class component — maintains the same public API as before.
 * Props accepted from consumers:
 *   rows, columns, enableFilter, minHeight, proofsFilters,
 *   handleGridSort, totalPages, handlePageIndexChange, handlePageSizeChange,
 *   totalCount, handleFilterChange, onRowSelected, onItemsUpdated
 *
 * Column format from consumers: { key: string, name: string, sortable?: bool, filterable?: bool, resizable?: bool, width?: number, filterRenderer?: Component }
 */
class Grid extends Component {
    constructor(props) {
        super(props);

        this.state = {
            rows: [],
            columns: [],
            filtersParam: {},
            pageSize: 10,
            pageIndex: 0,
            totalPages: 0,
            totalCount: 0,
            enableFilter: true,
            proofsFilters: {},
            selectedRow: {},
            sortColumns: [],
            showFilters: false,
        };
    }

    componentDidMount() {
        console.log('Grid Mounting...');
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (
            nextProps.rows !== prevState._prevRows ||
            nextProps.columns !== prevState._prevColumns ||
            nextProps.totalPages !== prevState._prevTotalPages ||
            nextProps.totalCount !== prevState._prevTotalCount
        ) {
            return {
                rows: nextProps.rows || [],
                columns: nextProps.columns,
                totalPages: nextProps.totalPages,
                totalCount: nextProps.totalCount,
                proofsFilters: nextProps.proofsFilters
                    ? nextProps.proofsFilters
                    : prevState.proofsFilters,
                _prevRows: nextProps.rows,
                _prevColumns: nextProps.columns,
                _prevTotalPages: nextProps.totalPages,
                _prevTotalCount: nextProps.totalCount,
            };
        }
        return null;
    }

    pageChanged(data) {
        const page = data.selected;
        this.setState({ pageIndex: page });
        if (this.props.handlePageIndexChange) {
            this.props.handlePageIndexChange(page);
        }
    }

    pageSizeChanged(pSize) {
        this.setState({ pageSize: pSize, pageIndex: 0 });
        if (this.props.handlePageSizeChange) {
            this.props.handlePageSizeChange(pSize);
        }
    }

    handleFilterChange(filter) {
        const newFilters = { ...this.state.filtersParam };
        if (filter.filterTerm) {
            newFilters[filter.column.key] = filter;
        } else {
            delete newFilters[filter.column.key];
        }
        this.setState({ filtersParam: newFilters }, () => {
            this.props.handleFilterChange(this.state.filtersParam);
        });
        return newFilters;
    }

    getFilteredRows() {
        const { rows, filtersParam } = this.state;
        if (!filtersParam || Object.keys(filtersParam).length === 0) {
            return rows;
        }
        return rows.filter(row => {
            return Object.keys(filtersParam).every(key => {
                const filter = filtersParam[key];
                if (!filter || !filter.filterTerm) return true;
                const cellValue = String(row[key] || '').toLowerCase();
                if (Array.isArray(filter.filterTerm)) {
                    return filter.filterTerm.some(
                        term => cellValue.includes(String(term.value || '').toLowerCase())
                    );
                }
                return cellValue.includes(String(filter.filterTerm).toLowerCase());
            });
        });
    }

    onRowSelected(row) {
        if (!row) return;
        var selectedRow = this.state.rows.find(item => item.id == row.id);
        this.setState({ selectedRow: selectedRow }, () => {
            this.props.onRowSelected(selectedRow);
        });
    }

    handleSortColumnsChange(newSortColumns) {
        this.setState({ sortColumns: newSortColumns, pageIndex: 0 });
        if (newSortColumns.length > 0) {
            const { columnKey, direction } = newSortColumns[0];
            this.props.handleGridSort(columnKey, direction);
        } else {
            this.props.handleGridSort(null, 'NONE');
        }
    }

    toggleFilters() {
        this.setState(prev => ({ showFilters: !prev.showFilters }));
    }

    EmptyRowsView() {
        const message = 'No Results Found';
        return (
            <div style={{ textAlign: 'center', padding: '200px' }}>
                <h3>{message}</h3>
            </div>
        );
    }

    /**
     * Clean columns: remove RDG-specific properties that react-table doesn't understand.
     * But keep filterRenderer info so GridTable knows which columns get dropdowns.
     */
    cleanColumns(columns) {
        if (!columns) return [];
        return columns.map(col => {
            const clean = { ...col };
            // Remove RDG-specific renderer props
            delete clean.filterRenderer;
            delete clean.filterable;
            delete clean.formatter;
            delete clean.renderCell;
            delete clean.renderEditCell;
            return clean;
        });
    }

    /**
     * Get valid filter values for multi-select columns.
     * Uses proofsFilters data from server for known columns,
     * or extracts unique values from current rows.
     */
    getValidFilterValues(columnId) {
        if (columnId === 'createdBy' || columnId === 'modifiedBy' || columnId === 'isEnabled') {
            var filtersData = this.state.proofsFilters;
            return filtersData ? filtersData[columnId] : [];
        }
        var r = (this.state.rows || [])
            .map(r => r[columnId])
            .filter((item, i, a) => {
                return i === a.indexOf(item);
            });
        return r;
    }

    render() {
        const filteredRows = this.getFilteredRows();
        const cleanedColumns = this.cleanColumns(this.state.columns);

        // Build filterValues map for multi-select columns
        const filterValues = {};
        if (this.state.columns) {
            this.state.columns.forEach(col => {
                if (col.filterRenderer) {
                    filterValues[col.key] = this.getValidFilterValues(col.key);
                }
            });
        }

        return (
            <>
                <Pagination
                    totalPages={this.state.totalPages}
                    pageChanged={this.pageChanged.bind(this)}
                    pageSizeChanged={this.pageSizeChanged.bind(this)}
                    selectedPage={this.state.pageIndex}
                    totalCount={this.state.totalCount}
                />
                <GridTable
                    columns={cleanedColumns}
                    rows={filteredRows}
                    sortColumns={this.state.sortColumns}
                    onSortColumnsChange={this.handleSortColumnsChange.bind(this)}
                    onRowClick={this.onRowSelected.bind(this)}
                    minHeight={this.props.minHeight}
                    noRowsFallback={<this.EmptyRowsView />}
                    enableFilter={this.props.enableFilter}
                    showFilters={this.state.showFilters}
                    onToggleFilters={this.toggleFilters.bind(this)}
                    onFilterChange={this.handleFilterChange.bind(this)}
                    filterValues={filterValues}
                    originalColumns={this.state.columns}
                />
            </>
        );
    }
}

export { Grid };
